import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import React from "react";
import loadCategories, { type Section } from "../../../lib/load-categories";
import { type Article } from "../../../lib/load-category-articles";
import type { AppSettings } from "../../../lib/get-app-settings";
import { THelpCenterLightLayout } from "../../../lib/load-company-settings";
import LightLayout from "../../../components/layout.light";
import getObjectByAlias from "../../../lib/get-object-by-alias";
import loadAllArticles from "../../../lib/load-all-articles";
import {
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
} from "../../../lib/api";
import findFirstArticle from "../../../lib/find-first-article";
import getBaseUrl from "../../../lib/get-base-url";
import getRedirectUrl from "../../../lib/get-redirect-url";

export type TCategoryLightPageProps = {
  appSettings: AppSettings;
  allArticles: Article[];
  categories: Section[];
};

const CategoryLightPage: NextPage<TCategoryLightPageProps> = ({
  appSettings,
  allArticles,
  categories,
}) => {
  return (
    <LightLayout
      appSettings={appSettings}
      hasTitle
      categories={categories}
      articles={allArticles}
    >
      <h2>No articles</h2>
    </LightLayout>
  );
};

export default CategoryLightPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterLightLayout }
): Promise<ReturnType<GetServerSideProps<TCategoryLightPageProps>>> => {
  const pathSansSearchParams = context.resolvedUrl.split("?")[0];
  if (pathSansSearchParams !== "/") {
    try {
      const object = await getObjectByAlias(
        appSettings.companyId,
        context.resolvedUrl,
        context
      );

      if (object) {
        return {
          redirect: {
            statusCode: 302,
            destination: getRedirectUrl(object.path, context),
          },
        };
      }
    } catch (error: any) {
      // Fall through
    }
  }

  try {
    const baseUrl = getBaseUrl(appSettings);

    const [allCategories = [], allArticles = []] = await Promise.all([
      loadCategories(appSettings.slug, context),
      loadAllArticles(appSettings.slug, context),
    ]);

    const category = allCategories.find(
      (c) => c.slug === context.params?.categorySlug
    );

    const firstArticle = findFirstArticle(
      allCategories,
      allArticles,
      category?.id
    );

    if (firstArticle) {
      return {
        redirect: {
          statusCode: 302,
          destination: getRedirectUrl(
            `${baseUrl}/articles/${firstArticle.slug}`,
            context
          ),
        },
      };
    }

    const props: TCategoryLightPageProps = {
      appSettings,
      allArticles,
      categories: allCategories.filter((category) => !category.parentId),
    };

    return { props };
  } catch (error: any) {
    console.error(error);
    if (
      error instanceof UnauthorizedError ||
      error instanceof ForbiddenError ||
      error instanceof NotFoundError
    ) {
      throw error;
    }
  }

  return {
    redirect: { statusCode: 302, destination: getRedirectUrl("/", context) },
  };
};
